import EgressHelper from '@livekit/egress-sdk';
import { LiveKitRoom } from '@livekit/components-react';
import { useState, useEffect, useCallback } from 'react';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import Stack from './Stack';
import { RecordingProvider } from './RecordingContext';
import { useLogger } from '@ui/shared/components/Logger';
import { LOGGER_LABEL } from './utils/constants';
import MissingParametersForm from './components/MissingParametersForm';

/**
 * Helper function to parse URL query parameters
 * @returns {Object} Object containing the query parameters
 */
const useQuery = () => {
  return new URLSearchParams(useLocation().search);
};

/**
 * Recording component that validates URL parameters and renders the LiveKit room
 *
 * Required URL parameters:
 * - url: LiveKit server URL
 * - token: Access token for the LiveKit server
 * - room: Room name to connect to
 *
 * Optional URL parameters:
 * - showTrackSid: Set to "true" to show track SIDs on video overlays (default: false)
 *
 * Route parameters:
 * - installSid: Installation SID
 * - examSid: Exam SID
 * - sessionSid: Session SID
 *
 * @returns {React.ReactElement} The rendered component
 */
const Recording = () => {
  const [error, setError] = useState(null);
  const [missingParams, setMissingParams] = useState([]);
  const [retryCount, setRetryCount] = useState(0);
  const MAX_RETRIES = 3;

  // Get route parameters
  const { installSid, examSid, sessionSid, spMediaAuthToken } = useParams();
  const logger = useLogger();
  const navigate = useNavigate();
  const location = useLocation();

  logger.setLogLevel('debug');

  // Get query parameters
  const query = useQuery();
  const showTrackSid = query.get('showTrackSid') === 'true';

  // Handle retry - MUST be defined before any conditional returns
  const handleRetry = useCallback(() => {
    if (retryCount < MAX_RETRIES) {
      logger.info(LOGGER_LABEL, 'Retrying connection', {
        attempt: retryCount + 1,
        maxRetries: MAX_RETRIES
      });
      setRetryCount(prev => prev + 1);
      setError(null);
      setMissingParams([]);
    } else {
      logger.error(LOGGER_LABEL, 'Max retry attempts reached', {
        maxRetries: MAX_RETRIES
      });
    }
  }, [retryCount, logger]);

  // Handle LiveKit errors - MUST be defined before any conditional returns
  const handleLiveKitError = useCallback((liveKitError) => {
    logger.error(LOGGER_LABEL, 'LiveKit room error', {
      error: liveKitError,
      errorCode: liveKitError?.code,
      errorMessage: liveKitError?.message,
      errorName: liveKitError?.name,
      installSid,
      examSid,
      sessionSid,
      retryCount
    });
    setError(liveKitError);
  }, [installSid, examSid, sessionSid, retryCount, logger]);

  // Handle form submission for missing parameters
  const handleParametersSubmit = useCallback((formValues) => {
    if (!formValues) {
      // User clicked retry without filling form
      handleRetry();
      return;
    }

    logger.info(LOGGER_LABEL, 'Updating URL with provided parameters', {
      hasUrl: !!formValues.url,
      hasToken: !!formValues.token,
      hasRoom: !!formValues.room
    });

    // Build new query string with existing params + new values
    const searchParams = new URLSearchParams(location.search);

    if (formValues.url) {
      searchParams.set('url', formValues.url.trim());
    }
    if (formValues.token) {
      searchParams.set('token', formValues.token.trim());
    }
    if (formValues.room) {
      searchParams.set('room', formValues.room.trim());
    }

    // Navigate to same path with updated query params
    navigate({
      pathname: location.pathname,
      search: searchParams.toString()
    }, { replace: true });

    // Clear missing params to trigger re-validation
    setMissingParams([]);
    setError(null);
  }, [handleRetry, logger, location.pathname, location.search, navigate]);

  // Validate route and URL parameters
  useEffect(() => {
    const validateParams = () => {
      // First validate route parameters
      const routeErrors = [];
      if (!installSid) routeErrors.push('installSid');
      if (!examSid) routeErrors.push('examSid');
      if (!sessionSid) routeErrors.push('sessionSid');
      if (!spMediaAuthToken) routeErrors.push('spMediaAuthToken');

      if (routeErrors.length > 0) {
        const errorMsg = `Missing required route parameters: ${routeErrors.join(', ')}`;
        logger.error(LOGGER_LABEL, errorMsg, {
          hasInstallSid: !!installSid,
          hasExamSid: !!examSid,
          hasSessionSid: !!sessionSid,
          hasToken: !!spMediaAuthToken
        });
        setError(new Error(errorMsg));
        return;
      }

      // Set metadata for all subsequent logs
      logger.setMetadata({ installSid, examSid, sessionSid });
      logger.info(LOGGER_LABEL, 'Route parameters validated');

      // Now validate LiveKit URL parameters
      // Only url and token are REQUIRED for LiveKit connection
      // Room is optional (egress knows the room internally)
      const requiredParams = ['url', 'token'];
      const missing = [];

      try {
        const url = EgressHelper.getLiveKitURL();
        const token = EgressHelper.getAccessToken();
        const room = query.get('room');

        if (!url) {
          missing.push('url');
          logger.error(LOGGER_LABEL, 'LiveKit URL is missing or invalid');
        } else {
          logger.debug(LOGGER_LABEL, 'LiveKit URL validated', { url });
        }

        if (!token) {
          missing.push('token');
          logger.error(LOGGER_LABEL, 'LiveKit access token is missing or invalid', { url });
        } else {
          logger.debug(LOGGER_LABEL, 'LiveKit access token validated');
        }

        // Room is optional - log but don't block
        if (!room) {
          logger.debug(LOGGER_LABEL, 'LiveKit room name not provided (optional for egress)');
        } else {
          logger.debug(LOGGER_LABEL, 'LiveKit room name validated', { room });
        }

        setMissingParams(missing);

        if (missing.length > 0) {
          logger.warn(LOGGER_LABEL, 'Required LiveKit parameters missing', { missing, url });
        } else {
          logger.info(LOGGER_LABEL, 'All parameters validated successfully');
        }
      } catch (err) {
        logger.error(LOGGER_LABEL, 'Failed to validate LiveKit parameters', {
          error: err,
          errorMessage: err.message,
          errorStack: err.stack
        });
        setError(err);
        setMissingParams(requiredParams);
      }
    };
    validateParams();
  }, [installSid, examSid, sessionSid, spMediaAuthToken, logger]);

  // Show error early if route params missing
  if (error && error.message.includes('route parameters')) {
    return (
      <div
        className="error-container"
        style={{ padding: '20px', maxWidth: '600px', margin: '0 auto' }}
      >
        <h2>Configuration Error</h2>
        <p style={{ color: 'red' }}>{error.message}</p>
        <p>Please check the URL and ensure all required parameters are present.</p>
        <details style={{ marginTop: '20px' }}>
          <summary>Technical Details</summary>
          <pre style={{ background: '#f5f5f5', padding: '10px', overflow: 'auto' }}>
            {JSON.stringify({
              installSid,
              examSid,
              sessionSid,
              hasToken: !!spMediaAuthToken
            }, null, 2)}
          </pre>
        </details>
      </div>
    );
  }

  // If there are missing LiveKit parameters, show the form
  if (missingParams.length > 0) {
    return (
      <MissingParametersForm
        missingParams={missingParams}
        onSubmit={handleParametersSubmit}
        retryCount={retryCount}
        maxRetries={MAX_RETRIES}
      />
    );
  }

  // If we have all required parameters, render the LiveKit room
  try {
    const url = EgressHelper.getLiveKitURL();
    const token = EgressHelper.getAccessToken();

    logger.info(LOGGER_LABEL, 'Initializing LiveKit room', {
      serverUrl: url,
      hasToken: !!token,
      installSid,
      examSid,
      sessionSid
    });

    return (
      <LiveKitRoom
        serverUrl={url}
        token={token}
        onError={handleLiveKitError}
        onConnected={() => {
          logger.info(LOGGER_LABEL, 'Successfully connected to LiveKit room');
          setRetryCount(0); // Reset retry count on successful connection
        }}
        onDisconnected={(reason) => {
          logger.warn(LOGGER_LABEL, 'Disconnected from LiveKit room', { reason });
        }}
      >
        <RecordingProvider
          installSid={installSid}
          examSid={examSid}
          sessionSid={sessionSid}
          spMediaAuthToken={spMediaAuthToken}
          showTrackSid={showTrackSid}
        >
          {error ? (
            <div className="error" style={{ padding: '20px', textAlign: 'center', color: 'white' }}>
              <h3>Connection Error</h3>
              <p>{error.message}</p>
              {retryCount < MAX_RETRIES && (
                <button
                  onClick={handleRetry}
                  style={{ padding: '8px 16px', marginTop: '10px', cursor: 'pointer' }}
                >
                  Retry Connection ({retryCount + 1}/{MAX_RETRIES})
                </button>
              )}
            </div>
          ) : (
            <Stack />
          )}
        </RecordingProvider>
      </LiveKitRoom>
    );
  } catch (err) {
    logger.error(LOGGER_LABEL, 'Error initializing Recording', {
      error: err,
      errorMessage: err.message,
      errorStack: err.stack,
      installSid,
      examSid,
      sessionSid
    });
    return (
      <div className="error" style={{ padding: '20px', textAlign: 'center' }}>
        <h3>Error initializing Recording</h3>
        <p>{err.message}</p>
        {retryCount < MAX_RETRIES && (
          <button
            onClick={handleRetry}
            style={{ padding: '8px 16px', marginTop: '10px', cursor: 'pointer' }}
          >
            Retry ({retryCount + 1}/{MAX_RETRIES})
          </button>
        )}
        <details style={{ marginTop: '20px' }}>
          <summary>Technical Details</summary>
          <pre style={{ background: '#f5f5f5', padding: '10px', overflow: 'auto', textAlign: 'left' }}>
            {JSON.stringify({
              errorName: err.name,
              errorMessage: err.message,
              installSid,
              examSid,
              sessionSid,
              retryCount
            }, null, 2)}
          </pre>
        </details>
      </div>
    );
  }
};

export default Recording;
